import { useState, useCallback, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Search, User } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useCreateSiblingSession } from '@/hooks/useCreateSiblingSession';
import { useCurrentAppUserId } from '@/hooks/useCurrentAppUser';
import { DEMAND_TYPE_CONFIG } from '@/hooks/useSupportInbox';
import { useDebounce } from '@/hooks/useDebounce';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { DemandType } from '@/types/support.types';

interface Contact {
  id: string;
  first_name: string | null;
  last_name: string | null;
  primary_phone: string | null;
}

interface NewSessionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSessionCreated: (sessionId: string) => void;
  prefilledContactId?: string | null;
  preTicketId?: string | null;
}

export function NewSessionModal({ open, onOpenChange, onSessionCreated, prefilledContactId, preTicketId }: NewSessionModalProps) {
  const [search, setSearch] = useState('');
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [demandType, setDemandType] = useState<DemandType>('status');
  const debouncedSearch = useDebounce(search, 300);
  const currentAppUserId = useCurrentAppUserId();
  const queryClient = useQueryClient();
  const { createSiblingSession, isCreating } = useCreateSiblingSession();

  // Auto-load prefilled contact
  const { data: prefilledContact, isLoading: isLoadingPrefill } = useQuery({
    queryKey: ['contact-prefill', prefilledContactId],
    queryFn: async () => {
      if (!prefilledContactId) return null;
      const { data, error } = await supabase
        .from('contacts')
        .select('id, first_name, last_name, primary_phone')
        .eq('id', prefilledContactId)
        .single();
      if (error) throw error;
      return data as Contact;
    },
    enabled: !!prefilledContactId && open,
  });

  // Auto-set prefilled contact when loaded (useEffect — sem anti-pattern)
  useEffect(() => {
    if (prefilledContact) {
      setSelectedContact(prefilledContact);
    }
  }, [prefilledContact]);

  const { data: contacts = [], isLoading: isSearching } = useQuery({
    queryKey: ['contacts-search', debouncedSearch],
    queryFn: async () => {
      if (!debouncedSearch || debouncedSearch.length < 2) return [];
      const { data, error } = await supabase
        .rpc('search_contacts_unaccent', {
          search_term: debouncedSearch.trim(),
          max_results: 10,
        });
      if (error) throw error;
      return (data || []) as Contact[];
    },
    enabled: open && debouncedSearch.length >= 2,
  });

  const [isConverting, setIsConverting] = useState(false);

  const handleCreate = useCallback(async () => {
    if (!selectedContact) return;
    try {
      // If converting a pre-ticket, update it instead of creating new
      if (preTicketId) {
        setIsConverting(true);
        const { error } = await supabase
          .from('support_sessions')
          .update({
            demand_type: demandType as any,
            status: 'in_progress' as any,
            inactivity_deadline: null,
          })
          .eq('id', preTicketId);
        
        if (error) throw error;
        onSessionCreated(preTicketId);
        queryClient.invalidateQueries({ queryKey: ['support-sessions'], refetchType: 'all' });
        queryClient.invalidateQueries({ queryKey: ['support-contact-sessions-full'] });
        queryClient.invalidateQueries({ queryKey: ['contact-sessions'] });
        setIsConverting(false);
      } else {
        const sessionId = await createSiblingSession({
          contactId: selectedContact.id,
          demandType,
          assignedTo: currentAppUserId || undefined,
        });
        onSessionCreated(sessionId);
      }
      onOpenChange(false);
      setSearch('');
      setSelectedContact(null);
      setDemandType('status');
    } catch {
      setIsConverting(false);
      // Error handled by mutation
    }
  }, [selectedContact, demandType, currentAppUserId, createSiblingSession, onSessionCreated, onOpenChange, preTicketId]);

  const contactName = (c: Contact) =>
    [c.first_name, c.last_name].filter(Boolean).join(' ') || 'Sem nome';

  const hasPrefilledContact = !!prefilledContactId;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{preTicketId ? 'Classificar Pré-chamado' : 'Novo Chamado de Atendimento'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Contact section */}
          {hasPrefilledContact ? (
            // Contato pré-definido: nunca mostra busca
            isLoadingPrefill ? (
              <div className="flex items-center gap-2 bg-accent/50 rounded-lg px-3 py-2">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Carregando contato...</span>
              </div>
            ) : selectedContact ? (
              <div className="flex items-center gap-2 bg-accent/50 rounded-lg px-3 py-2">
                <User className="h-4 w-4 text-muted-foreground shrink-0" />
                <div>
                  <p className="text-sm font-medium">{contactName(selectedContact)}</p>
                  {selectedContact.primary_phone && (
                    <p className="text-xs text-muted-foreground">{selectedContact.primary_phone}</p>
                  )}
                </div>
              </div>
            ) : null
          ) : (
            // Sem prefill: fluxo normal de busca
            !selectedContact ? (
              <div className="space-y-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar contato por nome ou telefone..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>

                {isSearching && (
                  <div className="flex justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                )}

                {contacts.length > 0 && (
                  <div className="max-h-48 overflow-y-auto border rounded-lg divide-y">
                    {contacts.map((c) => (
                      <button
                        key={c.id}
                        className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-accent transition-colors"
                        onClick={() => setSelectedContact(c)}
                      >
                        <User className="h-4 w-4 text-muted-foreground shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{contactName(c)}</p>
                          {c.primary_phone && <p className="text-xs text-muted-foreground">{c.primary_phone}</p>}
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {debouncedSearch.length >= 2 && !isSearching && contacts.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-3">
                    Nenhum contato encontrado
                  </p>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-between bg-accent/50 rounded-lg px-3 py-2">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">{contactName(selectedContact)}</p>
                    {selectedContact.primary_phone && (
                      <p className="text-xs text-muted-foreground">{selectedContact.primary_phone}</p>
                    )}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setSelectedContact(null); setSearch(''); }}
                >
                  Trocar
                </Button>
              </div>
            )
          )}

          {/* Demand type */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Tipo de demanda</label>
            <Select value={demandType} onValueChange={(v) => setDemandType(v as DemandType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(DEMAND_TYPE_CONFIG).map(([key, config]) => (
                  <SelectItem key={key} value={key}>
                    {config.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Create button */}
          <Button
            className="w-full"
            disabled={!selectedContact || isCreating || isConverting}
            onClick={handleCreate}
          >
            {(isCreating || isConverting) && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {preTicketId ? 'Classificar Chamado' : 'Criar Chamado'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
